import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  Tooltip,
  Badge,
  Divider,
  Tag,
  theme,
  ConfigProvider,
  Space,
  Typography,
  Grid
} from 'antd';
import {
  DashboardOutlined,
  ProductOutlined,
  TagsOutlined,
  StockOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  NotificationOutlined,
  ToolOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
  PieChartOutlined,
  ContainerOutlined,
  FileDoneOutlined,
  RiseOutlined,
  TeamOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import './DashboardLayout.css';

// Import your page components
import DashboardPage from './DashboardPage';
import ProductsPage from './ProductsPage';
import ProductStocks from './ProductStocks';
import BannersPage from './BannersPage';
import BlogsPage from './BlogPage';
import CategoriesPage from './CategoriesPage';
import SalesManagement from './SalesManagement';
import ViewReceiptPage from './ViewReceiptPage';
import GoodsReceivingForm from './GoodsReceivingForm';
import FinishedGoodsCustomerDeliveryForm from './FinishedGoodsCustomerDeliveryForm';
import RawMaterialManagement from './StoreIssueVoucher';
import RawMaterialReceivingNote from './RawMaterialReceivingNote';
import RawMaterialStockManagement from './RawMaterialStockManagement';
import ProfileEditPage from './ProfileEditPage';
import AnalyticsDashboard from './AnalyticsDashboard';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const DashboardLayout = () => {
  const [activeMenu, setActiveMenu] = useState(() => 
    localStorage.getItem('activeMenu') || 'dashboard-page'
  );
  const [adminData, setAdminData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState(5);
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const navigate = useNavigate();

  // Ant Design menu items configuration
  const menuItems = useMemo(() => [
    {
      key: 'dashboard-page',
      icon: <DashboardOutlined />,
      label: 'Dashboard'
    },
    {
      type: 'divider'
    },
    {
      key: 'product-management',
      icon: <AppstoreOutlined />,
      label: 'Product Management',
      children: [
        {
          key: 'categories',
          icon: <TagsOutlined />,
          label: 'Categories'
        },
        {
          key: 'products',
          icon: <ProductOutlined />,
          label: 'Products'
        },
        {
          key: 'stock',
          icon: <StockOutlined />,
          label: 'Product Stock'
        },
        {
          key: 'goods-receiving-form',
          icon: <InboxOutlined />,
          label: 'Receiving Goods'
        },
        {
          key: 'sales',
          icon: <ShoppingCartOutlined />,
          label: 'Sales & Delivery'
        },
        {
          key: 'view-receipt',
          icon: <FileDoneOutlined />,
          label: 'View Receipt'
        }
      ]
    },
    {
      type: 'divider'
    },
    {
      key: 'marketing-promotion',
      icon: <RiseOutlined />,
      label: 'Marketing & Promotion',
      children: [
        {
          key: 'blogs',
          icon: <FileTextOutlined />,
          label: 'Blogs'
        },
        {
          key: 'banners',
          icon: <NotificationOutlined />,
          label: 'Promotional Banners'
        }
      ]
    },
    {
      type: 'divider'
    },
    {
      key: 'raw-materials',
      icon: <ToolOutlined />,
      label: 'Raw Materials',
      children: [
        {
          key: 'raw_material_receiving_note',
          icon: <ContainerOutlined />,
          label: 'Raw Materials'
        },
        {
          key: 'raw-materials-issue',
          icon: <DatabaseOutlined />,
          label: 'Materials Issue'
        },
        {
          key: 'raw_stock',
          icon: <StockOutlined />,
          label: 'Materials Stock'
        }
      ]
    },
    {
      type: 'divider'
    },
    
  ], []);

  // Persistent menu state
  useEffect(() => {
    localStorage.setItem('activeMenu', activeMenu);
  }, [activeMenu]);

  // Admin authentication and data fetching
 const fetchAdminData = async () => {
  try {
    const response = await axios.get('https://agpipefitting.org.et/api/admin/profile', {
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
    });
    setAdminData(response.data);
    localStorage.setItem('admin', JSON.stringify(response.data));
  } catch (error) {
    console.error("Authentication error:", error.response?.data || error.message);
    // Only logout if 401 Unauthorized
    if (error.response?.status === 401) handleLogout();
  }
};


  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      handleLogout();
      return;
    }

    const storedAdmin = localStorage.getItem('admin');
    storedAdmin ? setAdminData(JSON.parse(storedAdmin)) : fetchAdminData();
  }, [navigate]);

  // Profile update handler
  const handleProfileUpdate = (updatedData) => {
    setAdminData(updatedData);
    localStorage.setItem('admin', JSON.stringify(updatedData));
  };

  // Logout functionality
  const handleLogout = () => {
    ['adminToken', 'admin', 'role', 'activeMenu'].forEach(item => 
      localStorage.removeItem(item)
    );
    setAdminData(null);
    setActiveMenu('dashboard-page');
    navigate('/admin-login');
  };

  // Content renderer
  const renderContent = () => {
    switch (activeMenu) {  
      case "dashboard-page": return <DashboardPage />;
      case "edit-profile": return <ProfileEditPage 
                                  adminData={adminData} 
                                  onProfileUpdate={handleProfileUpdate} />;
      case "products": return <ProductsPage />;
      case "stock": return <ProductStocks />;
      case "banners": return <BannersPage />;
      case "blogs": return <BlogsPage />;
      case "categories": return <CategoriesPage />;
      case "sales": return <SalesManagement />;
      case "view-receipt": return <ViewReceiptPage />;
      case "goods-receiving-form": return <GoodsReceivingForm />;
      case "goods-delivery-form": return <FinishedGoodsCustomerDeliveryForm />;
      case "raw-materials-issue": return <RawMaterialManagement />;
      case "raw_material_receiving_note": return <RawMaterialReceivingNote />;
      case "raw_stock": return <RawMaterialStockManagement />;
      case "/admin/analytics": return <AnalyticsDashboard />;
      default: return <DashboardPage />;
    }
  };

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => setActiveMenu('edit-profile')
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings'
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout
    }
  ];

  // Get active menu label
  const getActiveMenuLabel = () => {
    const findLabel = (items) => {
      for (const item of items) {
        if (item.key === activeMenu) return item.label;
        if (item.children) {
          const found = findLabel(item.children);
          if (found) return found;
        }
      }
      return 'Dashboard';
    };
    return findLabel(menuItems);
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 8,
          colorBgContainer: '#ffffff',
          colorBgLayout: '#f5f5f5',
          colorBorderSecondary: '#f0f0f0'
        },
        components: {
          Layout: {
            headerBg: '#ffffff',
            headerHeight: 64,
            headerPadding: '0 24px',
            siderBg: '#001529'
          },
          Menu: {
            itemBg: 'transparent',
            itemSelectedBg: '#1677ff',
            itemHoverBg: 'rgba(255, 255, 255, 0.1)',
            itemMarginBlock: 8,
            itemBorderRadius: 6
          }
        }
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        {/* Sidebar */}
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={256}
          style={{
            background: token.colorBgContainer,
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.06)',
            borderRight: `1px solid ${token.colorBorderSecondary}`
          }}
          breakpoint="lg"
          collapsedWidth={screens.lg ? 80 : 0}
          onBreakpoint={(broken) => {
            if (!screens.lg) setCollapsed(true);
          }}
        >
          {/* Logo */}
          <div style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '0 24px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`
          }}>
            {collapsed ? (
              <div style={{
                width: 32,
                height: 32,
                background: token.colorPrimary,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <GlobalOutlined style={{ color: '#fff', fontSize: 16 }} />
              </div>
            ) : (
              <Space align="center">
                <div style={{
                  width: 32,
                  height: 32,
                  background: token.colorPrimary,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GlobalOutlined style={{ color: '#fff', fontSize: 16 }} />
                </div>
                <Title level={4} style={{ margin: 0, fontSize: 18, color: token.colorTextHeading }}>
                  AG Pipe Fitting
                </Title>
              </Space>
            )}
          </div>

          {/* Menu */}
          <Menu
            mode="inline"
            selectedKeys={[activeMenu]}
            defaultOpenKeys={['product-management', 'marketing-promotion', 'raw-materials']}
            items={menuItems}
            style={{
              border: 'none',
              padding: '16px 12px',
              background: 'transparent'
            }}
            onClick={({ key }) => setActiveMenu(key)}
          />
        </Sider>

        <Layout>
          {/* Header */}
          <Header style={{
            padding: '0 24px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)'
          }}>
            <Space>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: 16 }}
              />
              
              <Space>
                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                  {getActiveMenuLabel()}
                </Title>
                {adminData?.role && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    {adminData.role}
                  </Tag>
                )}
              </Space>
            </Space>

            <Space size="large">
              {/* Notification */}
              <Tooltip title="Notifications">
                <Badge count={notifications} size="small">
                  <Button
                    type="text"
                    icon={<BellOutlined style={{ fontSize: 16 }} />}
                    shape="circle"
                  />
                </Badge>
              </Tooltip>

              {/* Help */}
              <Tooltip title="Help & Documentation">
                <Button
                  type="text"
                  icon={<QuestionCircleOutlined style={{ fontSize: 16 }} />}
                  shape="circle"
                />
              </Tooltip>

              {/* User Profile */}
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Space style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>
                  <Avatar
                    size="default"
                    icon={<UserOutlined />}
                    style={{
                      background: `linear-gradient(135deg, ${token.colorPrimary}, #722ed1)`
                    }}
                  />
                  {!collapsed && screens.md && (
                    <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
                      <Text strong style={{ fontSize: 14 }}>
                        {adminData?.name || 'Admin User'}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {adminData?.email || 'admin@agpipefitting.org.et'}
                      </Text>
                    </Space>
                  )}
                </Space>
              </Dropdown>
            </Space>
          </Header>

          {/* Content Area */}
          <Content style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
          }}>
            {renderContent()}
          </Content>

          {/* Footer */}
          <div style={{
            textAlign: 'center',
            padding: '16px 24px',
            color: token.colorTextSecondary,
            fontSize: 12,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorBgContainer
          }}>
            <Space direction="vertical" size={2}>
              <Text type="secondary">
                © {new Date().getFullYear()} AG Pipe Fitting. All rights reserved.
              </Text>
              <Text type="secondary">
                Version 2.1.0 • Last updated: Today
              </Text>
            </Space>
          </div>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default DashboardLayout;